import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import api from '@/lib/axios';
import { getInitials } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { useChatStore } from '@/store/chat.store';
import { usePresencia } from '@/hooks/usePresencia';
import { useNotificaciones } from '@/hooks/useNotificaciones';
import { useIkalStore } from '@/store/ikal.store';
import { IkalOrb } from '@/components/ikal/IkalOrb';

interface Medico {
  id: string;
  nombre: string;
  especialidad?: { nombre: string };
  usuario?: { fotoUrl?: string };
}

interface Contacto {
  id: string;
  medicoSolicitanteId: string;
  medicoReceptorId: string;
  medicoSolicitante: Medico;
  medicoReceptor: Medico;
  estado: string;
}

const SIDEBAR_BG = '#0f172a';

export function ContactosSidebar() {
  const usuario = useAuthStore(s => s.usuario);
  const esMedico =
    usuario?.rolId === ROL.MEDICO ||
    usuario?.rolId === ROL.MEDICO_ADMIN ||
    usuario?.rolId === ROL.SUPER_ADMIN;

  const { openChat } = useChatStore();
  const { open: ikalOpen, openIkal } = useIkalStore();
  const chatMedicoId = useChatStore(s => s.medicoId);
  const onlineIds = usePresencia();
  const { notificaciones, marcarLeida } = useNotificaciones();

  // Set de medicoIds con mensajes no leídos
  const unreadChatIds = useMemo(() => {
    const ids = new Set<string>();
    notificaciones
      .filter(n => !n.leida && n.tipo === 'CHAT_MENSAJE' && n.metadatos?.medicoId)
      .forEach(n => ids.add(n.metadatos!.medicoId as string));
    return ids;
  }, [notificaciones]);

  // Al abrir chat con un médico, limpiar sus notificaciones de chat no leídas
  useEffect(() => {
    if (!chatMedicoId) return;
    notificaciones
      .filter(n => !n.leida && n.tipo === 'CHAT_MENSAJE' && n.metadatos?.medicoId === chatMedicoId)
      .forEach(n => marcarLeida(n.id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMedicoId]);

  // Perfil propio para saber qué lado somos en cada contacto
  const { data: profile } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/api/auth/me').then(r => r.data),
    staleTime: 5 * 60_000,
  });
  const miMedicoId: string | undefined = profile?.medico?.id;

  const { data: contactosRaw = [] } = useQuery<Contacto[]>({
    queryKey: ['red-contactos'],
    queryFn: () => api.get('/api/red/contactos').then(r => r.data),
    enabled: esMedico,
    staleTime: 60_000,
  });

  if (!esMedico) return null;

  // Extraer el "otro médico" de cada relación de contacto
  const contactos: Medico[] = contactosRaw
    .filter(c => c.estado === 'aceptado')
    .map(c => (c.medicoSolicitanteId === miMedicoId ? c.medicoReceptor : c.medicoSolicitante))
    .filter(Boolean);

  // Ordenar: online primero (alfabético), luego offline (alfabético)
  const sorted = [...contactos].sort((a, b) => {
    const aOnline = onlineIds.has(a.id);
    const bOnline = onlineIds.has(b.id);
    if (aOnline !== bOnline) return aOnline ? -1 : 1;
    return a.nombre.localeCompare(b.nombre, 'es');
  });

  return (
    <Tooltip.Provider delayDuration={300}>
      <aside
        className="flex flex-col flex-shrink-0 rounded-[2rem] overflow-hidden"
        style={{ width: 56, backgroundColor: SIDEBAR_BG }}
      >
        {/* Header */}
        <div
          className="h-[60px] flex items-center justify-center flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <MessageCircle size={18} style={{ color: 'rgba(255,255,255,0.35)' }} />
        </div>

        {/* Ikal button */}
        <div className="px-2 pt-3 pb-1 flex justify-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div>
                <IkalOrb
                  state={ikalOpen ? 'talking' : 'idle'}
                  size={36}
                  onClick={() => openIkal()}
                  className="mb-1"
                />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="left"
                sideOffset={10}
                className="z-50 rounded-xl px-3 py-2 text-xs shadow-xl select-none animate-in fade-in-0 zoom-in-95"
                style={{ backgroundColor: '#1e293b', color: 'rgba(255,255,255,0.9)' }}
              >
                <p className="font-semibold">Ikal — Asistente IA</p>
                <Tooltip.Arrow style={{ fill: '#1e293b' }} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </div>

        {/* Lista de contactos */}
        <div className="flex-1 overflow-y-auto py-3 px-2 space-y-2 scrollbar-hide">
          {sorted.map(medico => {
            const online = onlineIds.has(medico.id);
            const foto = medico.usuario?.fotoUrl ?? null;
            const tieneNoLeidos = unreadChatIds.has(medico.id);

            return (
              <Tooltip.Root key={medico.id}>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={() => openChat(medico.id)}
                    className="relative w-9 h-9 rounded-full flex items-center justify-center transition-all outline-none"
                    style={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
                      const inner = e.currentTarget.firstElementChild as HTMLElement | null;
                      if (inner) inner.style.backgroundColor = 'rgba(255,255,255,0.18)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                      const inner = e.currentTarget.firstElementChild as HTMLElement | null;
                      if (inner) inner.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    }}
                  >
                    {/* Avatar con overflow-hidden propio para no recortar los badges */}
                    <div
                      className="absolute inset-0 rounded-full overflow-hidden flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    >
                      {foto ? (
                        <img
                          src={foto}
                          alt={medico.nombre}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitials(medico.nombre)
                      )}
                    </div>

                    {/* Badge de mensaje no leído — esquina superior derecha */}
                    {tieneNoLeidos && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
                        style={{ backgroundColor: '#ef4444', borderColor: SIDEBAR_BG }}
                      />
                    )}

                    {/* Indicador de presencia — esquina inferior derecha */}
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
                      style={{
                        backgroundColor: online ? '#22c55e' : '#475569',
                        borderColor: SIDEBAR_BG,
                      }}
                    />
                  </button>
                </Tooltip.Trigger>

                <Tooltip.Portal>
                  <Tooltip.Content
                    side="left"
                    sideOffset={10}
                    className="z-50 rounded-xl px-3 py-2 text-xs shadow-xl select-none animate-in fade-in-0 zoom-in-95"
                    style={{ backgroundColor: '#1e293b', color: 'rgba(255,255,255,0.9)', maxWidth: 200 }}
                  >
                    <p className="font-semibold leading-tight">Dr. {medico.nombre}</p>
                    {medico.especialidad && (
                      <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {medico.especialidad.nombre}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: online ? '#22c55e' : '#475569' }}
                      />
                      <span style={{ color: online ? '#86efac' : 'rgba(255,255,255,0.4)' }}>
                        {online ? 'En línea' : 'Desconectado'}
                      </span>
                    </div>
                    {tieneNoLeidos && (
                      <p className="text-[11px] mt-1.5 font-semibold" style={{ color: '#fca5a5' }}>
                        Mensaje nuevo
                      </p>
                    )}
                    <Tooltip.Arrow style={{ fill: '#1e293b' }} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            );
          })}

          {contactos.length === 0 && (
            <p
              className="text-[9px] text-center leading-tight px-1 pt-2"
              style={{ color: 'rgba(255,255,255,0.2)' }}
            >
              Sin contactos
            </p>
          )}
        </div>
      </aside>
    </Tooltip.Provider>
  );
}
